import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import BrandPresence from "@/components/BrandPresence";
import { getVentureBySlug } from "@/data/ventures";

// Shared reusable template for every individual brand route
// (/ventures/:slug): ONE unified full-width editorial banner (dark
// content panel + dominant brand image), detailed content, and a single
// "Official Brand Presence" section for every platform link.
const VenturePage = () => {
  const { slug } = useParams();
  const venture = getVentureBySlug(slug);

  if (!venture) {
    return <Navigate to="/ventures" replace />;
  }

  const {
    name, category, headline, expandedDescription, status, link,
    instagram, facebook, youtube, linkedin, logo, editorialImage, enquiryType,
  } = venture;
  const isLive = status === "live";

  return (
    <div data-testid={`page-venture-${slug}`}>
      <SEO title={name} description={expandedDescription} path={`/ventures/${slug}`} />

      {/* Unified full-width banner: one continuous composition, never two
          disconnected floating cards. */}
      <section className="brand-hero-banner" data-testid="brand-hero-banner">
        <div className="brand-hero-panel">
          <nav aria-label="Breadcrumb" className="mb-8">
            <Link to="/ventures" className="btn-outline-silver btn-pill-sm inline-flex" data-testid="venture-back-link">
              <ArrowLeft size={14} /> Return to Our Brands
            </Link>
          </nav>
          <Reveal>
            <img
              src={logo}
              alt={`${name} logo`}
              className="brand-hero-logo mb-8"
              style={{ filter: "drop-shadow(0 6px 24px rgba(0,0,0,0.55))" }}
            />
            <span className="eyebrow block mb-4" data-testid="venture-category">{category}</span>
            <h1 className="text-h2 mb-4">{name}</h1>
            <p className="font-cormorant italic text-h4 mb-6" style={{ color: "var(--gg-champagne)" }}>{headline}</p>
            <p className="text-body">{expandedDescription.slice(0, 180)}&hellip;</p>
          </Reveal>
        </div>
        <div className="brand-hero-image-wrap">
          <img src={editorialImage} alt={`${name} — editorial`} loading="lazy" />
        </div>
      </section>

      <section className="relative page-shell py-12 md:py-16 lg:py-20">
        <Reveal className="editorial-col">
          <p className="text-editorial text-justify">{expandedDescription}</p>
        </Reveal>
      </section>

      <section className="relative page-shell pb-20">
        <Reveal>
          <BrandPresence
            website={isLive ? link : null}
            instagram={instagram}
            facebook={facebook}
            youtube={youtube}
            linkedin={linkedin}
          />
        </Reveal>
      </section>

      <section className="relative py-14 md:py-20 lg:py-24 text-center" style={{ background: "var(--gg-midnight-black)" }}>
        <Reveal className="page-shell max-w-xl mx-auto">
          <h2 className="text-h3 mb-6">Have a Question About {name}?</h2>
          <Link
            to={`/contact?enquiry=${encodeURIComponent(enquiryType)}`}
            className="btn-outline-silver"
            data-testid="venture-contact-cta"
          >
            Begin a Conversation
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default VenturePage;
